import Button from "react-bootstrap/Button"
import { FiPlus, FiTrash2 } from "react-icons/fi"
import "./index.scss"

import { Title, Container, Text, Number, Select } from "components/Form"
import { REQUIRED_CONSUMABLE_TYPES } from "state_management/lims/limsSpecs"
import { CONSUMABLE_TYPE_FIELDS, STANDARD_CLASSES, standardClassLabelKey } from "state_management/lims/limsConsumables"
import useT from "utils/hooks/useT"

// RequirementsSection (F8 / SPEC-040) — edits the high-level CONSUMABLE REQUIREMENTS of an
// analysis definition: what KIND of consumable the run must use (type, optional quantity range,
// optional reference-standard class, free-text notes) — NOT a serial/lot number (those are chosen
// per run at result entry). It is a controlled component: the parent (AnalysisConfigurationPage)
// owns the `requirements` array and folds it onto the nested `spec` payload on save.
//
// Each requirement: { type, name, quantity: {min,max,unit}|null, standardClass|null, notes }.
// The requirements drive (a) hard filtering of the run-time consumable picker (only items that
// satisfy a requirement + are active/not-expired are selectable) and (b) the send-to-review
// completeness gate (every requirement must be covered by a selected consumable). Quantity is
// ADVISORY (documented to the stock location), never a hard gate.
export default function RequirementsSection({ requirements, onChange }) {
  const { t } = useT("common")

  // Type options reuse the consumable type labels (reagent / standard / column / glassware).
  const typeOptions = REQUIRED_CONSUMABLE_TYPES.map((type) => ({
    id: type,
    name: t(CONSUMABLE_TYPE_FIELDS[type].labelKey),
  }))
  // Reference-standard class options + an explicit "any class" (empty id → standardClass null).
  const standardClassOptions = [
    { id: "", name: t("limsRequiredConsumableAnyClass") },
    ...STANDARD_CLASSES.map((cls) => ({ id: cls, name: t(standardClassLabelKey(cls)) })),
  ]

  const updateRequirement = (idx, patch) => {
    onChange(requirements.map((req, i) => (i === idx ? { ...req, ...patch } : req)))
  }

  // Update one quantity sub-field; if every quantity field ends up blank, collapse to null (the
  // requirement carries no quantity — advisory, so an empty quantity is "unspecified", not 0).
  const updateQuantity = (idx, field, value) => {
    const req = requirements[idx]
    const nextQuantity = { ...(req.quantity || { min: null, max: null, unit: "" }), [field]: value }
    const empty =
      (nextQuantity.min === null || nextQuantity.min === "" || nextQuantity.min === undefined) &&
      (nextQuantity.max === null || nextQuantity.max === "" || nextQuantity.max === undefined) &&
      (!nextQuantity.unit || nextQuantity.unit.trim() === "")
    updateRequirement(idx, { quantity: empty ? null : nextQuantity })
  }

  const addRequirement = () => {
    onChange([
      ...requirements,
      { type: "reagent", name: "", quantity: null, standardClass: null, notes: "" },
    ])
  }

  const removeRequirement = (idx) => {
    onChange(requirements.filter((_req, i) => i !== idx))
  }

  return (
    <div className="RequirementsSection">
      <p className="RequirementsSection-help">{t("limsRequiredConsumablesHelp")}</p>
      <div className="RequirementsSection-rows">
        {requirements.length === 0 && (
          <p className="RequirementsSection-empty">{t("limsRequiredConsumablesEmpty")}</p>
        )}
        {requirements.map((req, idx) => {
          const quantity = req.quantity || { min: null, max: null, unit: "" }
          // The reference-standard class field is only meaningful for a "standard" requirement.
          const isStandard = req.type === "standard"
          return (
            <div className="RequirementsSection-row" key={idx}>
              <div className="RequirementsSection-type">
                <Container marginBottom="">
                  <Title>{t("limsRequiredConsumableType")}</Title>
                  <Select
                    isMulti={false}
                    collection={typeOptions}
                    value={req.type}
                    onChange={(values) => updateRequirement(idx, { type: values[0] || "reagent" })}
                    menuPlacement="bottom"
                  />
                </Container>
              </div>
              <Container marginBottom="">
                <Title>{t("limsRequiredConsumableName")}</Title>
                <Text
                  value={req.name}
                  onChange={(value) => updateRequirement(idx, { name: value })}
                  placeholder={t("limsRequiredConsumableNamePlaceholder")}
                />
              </Container>
              {isStandard && (
                <div className="RequirementsSection-class">
                  <Container marginBottom="">
                    <Title>{t("limsRequiredConsumableStandardClass")}</Title>
                    <Select
                      isMulti={false}
                      collection={standardClassOptions}
                      value={req.standardClass || ""}
                      onChange={(values) => updateRequirement(idx, { standardClass: values[0] || null })}
                      menuPlacement="bottom"
                    />
                  </Container>
                </div>
              )}
              <Container marginBottom="">
                <Title>{t("limsRequiredConsumableQuantityMin")}</Title>
                <Number value={quantity.min} onChange={(value) => updateQuantity(idx, "min", value)} />
              </Container>
              <Container marginBottom="">
                <Title>{t("limsRequiredConsumableQuantityMax")}</Title>
                <Number value={quantity.max} onChange={(value) => updateQuantity(idx, "max", value)} />
              </Container>
              <Container marginBottom="">
                <Title>{t("limsRequiredConsumableQuantityUnit")}</Title>
                <Text
                  value={quantity.unit || ""}
                  onChange={(value) => updateQuantity(idx, "unit", value)}
                  placeholder={t("limsRequiredConsumableQuantityUnitPlaceholder")}
                />
              </Container>
              <Container marginBottom="">
                <Title>{t("limsRequiredConsumableNotes")}</Title>
                <Text value={req.notes} onChange={(value) => updateRequirement(idx, { notes: value })} />
              </Container>
              <div className="RequirementsSection-remove">
                <Button variant="outline-danger" size="sm" onClick={() => removeRequirement(idx)}>
                  <FiTrash2 />
                </Button>
              </div>
            </div>
          )
        })}
      </div>
      <div className="RequirementsSection-add">
        <Button variant="link" onClick={addRequirement}>
          <FiPlus /> {t("limsRequiredConsumableAdd")}
        </Button>
      </div>
    </div>
  )
}
